/**
 * Frontend Logger Utility
 * Environment-based logging for production safety
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Logger instance with environment-aware methods
 */
const logger = {
  /**
   * Log debug messages (only in development)
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args);
    }
  },

  /**
   * Log info messages (only in development)
   * @param {...any} args - Arguments to log
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log('[INFO]', ...args);
    }
  },

  /**
   * Log info messages (alias for log)
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Log warning messages (all environments, but sanitized in production)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    } else {
      // In production, log warnings but sanitize sensitive data
      console.warn('[WARN]', ...sanitizeArgs(args));
    }
  },

  /**
   * Log error messages (all environments)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Log API errors with context
   * @param {string} context - Context of the error (e.g., 'Fetching users')
   * @param {Error} error - The error object
   */
  apiError: (context, error) => {
    if (isDevelopment) {
      console.group(`[API ERROR] ${context}`);
      console.error('Message:', error.message);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);
      console.error('Full Error:', error);
      console.groupEnd();
    } else {
      // In production, log minimal error info
      console.error('[API ERROR]', context, {
        status: error.response?.status,
        message: error.message
      });
    }
  },

  /**
   * Log performance metrics (only in development)
   * @param {string} label - Label for the metric
   * @param {number} duration - Duration in milliseconds
   */
  perf: (label, duration) => {
    if (isDevelopment) {
      console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
    }
  },

  /**
   * Log component lifecycle events (only in development)
   * @param {string} component - Component name
   * @param {string} event - Lifecycle event (mount, unmount, update)
   */
  component: (component, event) => {
    if (isDevelopment) {
      console.log(`[COMPONENT] ${component} - ${event}`);
    }
  },

  /**
   * Group logs (only in development)
   * @param {string} label - Group label
   */
  group: (label) => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * End log group (only in development)
   */
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Table logging (only in development)
   * @param {any} data - Data to display in table
   */
  table: (data) => {
    if (isDevelopment) {
      console.table(data);
    }
  }
};

/**
 * Sanitize arguments to remove sensitive information
 * @param {Array} args - Arguments to sanitize
 * @returns {Array} Sanitized arguments
 */
function sanitizeArgs(args) {
  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      return sanitizeObject(arg);
    }
    return arg;
  });
}

/**
 * Sanitize object by removing sensitive keys
 * @param {Object} obj - Object to sanitize
 * @returns {Object} Sanitized object
 */
function sanitizeObject(obj) {
  const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization', 'api_key'];
  const sanitized = { ...obj };
  
  for (const key in sanitized) {
    if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeObject(sanitized[key]);
    }
  }
  
  return sanitized;
}

/**
 * Disable all console methods in production (except error)
 * Call this in index.js or App.js entry point
 */
export const disableConsoleInProduction = () => {
  if (isProduction) {
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    // Keep console.error enabled for critical issues
  }
};

/**
 * Get environment info
 */
export const getEnvironment = () => ({
  isDevelopment,
  isProduction,
  nodeEnv: process.env.NODE_ENV
});

export default logger;
